import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  NO_PRESTATION_CAPABILITY_LABEL,
} from './equipeUi';
import {
  formatAbsenceBlock,
  getPresenceLabelForDay,
} from '../../utils/equipePlanningUtils';
import {
  filterWeekOperationalRdv,
  RDV_STATUS_UX,
} from '../../utils/equipeRdvRules';
import {
  formatParisDayHeader,
  formatParisTimeRange,
  getParisWeekDays,
  getParisWeekRangeLabel,
} from '../../utils/equipeWeekParis';

function overlapsDay(absence, ymd) {
  const start = new Date(absence.dateDebut);
  const end = new Date(absence.dateFin);
  const dayStart = new Date(`${ymd}T00:00:00`);
  const dayEnd = new Date(`${ymd}T23:59:59`);
  return start <= dayEnd && end >= dayStart;
}

export default function MemberWeekAgenda({
  member,
  weekOffset = 0,
  onWeekOffsetChange,
  horairesConfig,
  absences = [],
  rdvList = [],
  onRdvClick,
  loading = false,
  wideLayout = false,
}) {
  if (!member?.isIntervenant) {
    return (
      <p className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">
        {NO_PRESTATION_CAPABILITY_LABEL}.
      </p>
    );
  }

  const weekDays = getParisWeekDays(weekOffset);
  const weekRdv = filterWeekOperationalRdv(rdvList, weekDays);

  const rdvByDay = {};
  for (const day of weekDays) {
    rdvByDay[day.ymd] = weekRdv.filter((r) => {
      const ymd = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(
        new Date(r.dateDebut),
      );
      return ymd === day.ymd;
    });
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        Rendez-vous clients sur la semaine — les créneaux libres ne sont pas calculés ici.
      </p>

      <div className="flex items-center justify-between gap-2 rounded-xl border border-gray-100 bg-gray-50 px-2 py-2">
        <button
          type="button"
          onClick={() => onWeekOffsetChange?.(weekOffset - 1)}
          className="rounded-lg border border-gray-200 bg-white p-1.5 text-gray-600"
          aria-label="Semaine précédente"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-center text-xs font-semibold text-gray-800">
          Semaine du {getParisWeekRangeLabel(weekOffset)}
        </span>
        <button
          type="button"
          onClick={() => onWeekOffsetChange?.(weekOffset + 1)}
          className="rounded-lg border border-gray-200 bg-white p-1.5 text-gray-600"
          aria-label="Semaine suivante"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex h-32 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
        </div>
      ) : (
        <div
          className={
            wideLayout
              ? 'grid grid-cols-2 gap-3 xl:grid-cols-4 2xl:grid-cols-7'
              : 'space-y-4'
          }
        >
          {weekDays.map((day) => {
            const dayRdv = rdvByDay[day.ymd] || [];
            const presence = getPresenceLabelForDay(horairesConfig, day.jourSemaine);
            const dayAbsences = absences.filter((a) => overlapsDay(a, day.ymd));

            return (
              <section key={day.ymd} className="rounded-xl border border-gray-100 bg-white shadow-sm">
                <div className="border-b border-gray-50 px-3 py-2.5">
                  <p className="text-sm font-bold text-gray-900">{formatParisDayHeader(day.date)}</p>
                  {presence ? (
                    <p className="mt-0.5 text-xs text-emerald-700">Disponibilité : {presence}</p>
                  ) : (
                    <p className="mt-0.5 text-xs text-gray-400">Hors disponibilité</p>
                  )}
                </div>

                <div className="space-y-2 p-3">
                  {dayAbsences.map((a) => {
                    const { title, detail } = formatAbsenceBlock(a.dateDebut, a.dateFin, a.motif);
                    return (
                      <div
                        key={a.id}
                        className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
                      >
                        <p className="text-xs font-semibold text-amber-900">{title}</p>
                        <p className="text-xs text-amber-800">{detail}</p>
                      </div>
                    );
                  })}

                  {dayRdv.length === 0 && dayAbsences.length === 0 && !presence && (
                    <p className="text-xs text-gray-400">—</p>
                  )}

                  {dayRdv.map((rdv) => {
                    const ux = RDV_STATUS_UX[rdv.statut] || RDV_STATUS_UX.EN_ATTENTE;
                    return (
                      <button
                        key={rdv.id}
                        type="button"
                        onClick={() => onRdvClick?.(rdv)}
                        className="w-full rounded-lg border border-violet-100 bg-violet-50/60 px-3 py-2.5 text-left transition hover:border-violet-200 hover:bg-violet-50"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-xs font-semibold text-violet-900">
                            {formatParisTimeRange(rdv.dateDebut, rdv.dateFin)}
                          </p>
                          <span
                            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${ux.className}`}
                          >
                            {ux.label}
                          </span>
                        </div>
                        <p className="mt-1 text-sm font-medium text-gray-900">
                          {rdv.prestation?.nom || 'Prestation'}
                        </p>
                        <p className="text-xs text-gray-600">
                          {rdv.user?.prenom} {rdv.user?.nom}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
